<template>
  <gas-page-shell :breadcrumb="['设备管理', '通信报文']">
    <div class="stat-row">
      <div v-for="item in statCards" :key="item.key" class="stat-card">
        <div class="stat-icon" :style="{ background: item.bg, color: item.color }"><i :class="item.icon"></i></div>
        <div class="stat-body">
          <div class="stat-label">{{ item.label }}</div>
          <div class="stat-value" :style="{ color: item.valueColor }">{{ item.value }}</div>
        </div>
      </div>
    </div>

    <div class="search-section">
      <div class="section-title"><span class="title-bar"></span>检索条件</div>
      <el-form :inline="true" :model="query" size="small" label-width="80px" class="message-search-form">
        <el-form-item label="时间范围">
          <el-date-picker v-model="timeRange" type="datetimerange" range-separator="至"
            value-format="yyyy-MM-dd HH:mm:ss" start-placeholder="开始" end-placeholder="结束" style="width:360px"
            @change="onTimeChange" />
        </el-form-item>
        <el-form-item label="发送接收">
          <el-checkbox-group v-model="dirChecks" @change="onDirChange">
            <el-checkbox label="down">发</el-checkbox>
            <el-checkbox label="up">收</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="终端编码">
          <el-input v-model="query.terminalCode" clearable placeholder="请输入终端编码" style="width:160px" />
        </el-form-item>
        <el-form-item>
          <el-button icon="el-icon-refresh-left" @click="resetSearch">重置</el-button>
          <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
        </el-form-item>
        <el-form-item label="记录接收报文" class="record-switch-item">
          <el-switch
            v-model="recordReceiveEnabled"
            active-text="总开"
            inactive-text="总关"
            :disabled="recordSwitchSaving"
            @change="onRecordReceiveChange"
          />
          <el-button type="text" icon="el-icon-setting" class="device-switch-btn" @click="openReceiveConfigDialog">
            设备开关
          </el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="table-panel">
      <div class="table-head">
        <div class="table-title-group">
          <span class="table-title">报文列表 <em>共 {{ total }} 条记录</em></span>
          <gas-table-toolbar
            :columns="tableColumnDefs"
            :visible-map.sync="tableColumnVisible"
            :selected-count="selectedIds.length"
            :show-batch-delete="tableCanBatchDelete"
            @batch-delete="batchRemove"
          />
        </div>
        <div>
          <el-button type="text" icon="el-icon-download" @click="exportCsv">导出</el-button>
          <el-button type="text" icon="el-icon-refresh" @click="refreshAll" />
        </div>
      </div>

      <el-table :data="list" v-loading="loading" border size="small" @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="44" />
        <el-table-column label="ID" width="88" align="center">
          <template slot-scope="{ row }">#{{ row.messageId }}</template>
        </el-table-column>
        <el-table-column prop="createTime" label="时间" width="160" />
        <el-table-column label="终端编码" width="130">
          <template slot-scope="{ row }">
            <el-button type="text" class="code-link" @click="filterTerminal(row.terminalCode)">{{ row.terminalCode }}</el-button>
          </template>
        </el-table-column>
        <el-table-column label="发送接收" width="100" align="center">
          <template slot-scope="{ row }">
            <span class="dir-tag" :class="row.direction === 'down' ? 'send' : 'recv'">
              <i :class="row.direction === 'down' ? 'el-icon-s-promotion' : 'el-icon-download'"></i>
              {{ row.direction === 'down' ? '发送' : '接收' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="报文内容" min-width="280" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ hexContent(row) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="140" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" icon="el-icon-document-copy" @click="copyRow(row)">复制</el-button>
            <el-button type="text" size="small" icon="el-icon-view" @click="viewDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination class="pager" background layout="total, sizes, prev, pager, next, jumper"
        :total="total" :current-page="query.pageNum" :page-size="query.pageSize"
        :page-sizes="[10, 20, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <el-dialog title="报文详情" :visible.sync="detailVisible" width="620px">
      <el-descriptions v-if="detail" :column="1" border size="small">
        <el-descriptions-item label="终端编码">{{ detail.terminalCode }}</el-descriptions-item>
        <el-descriptions-item label="方向">{{ detail.direction === 'down' ? '发送' : '接收' }}</el-descriptions-item>
        <el-descriptions-item label="协议">{{ detail.protocolType || '—' }}</el-descriptions-item>
        <el-descriptions-item label="类型">{{ detail.messageType || '—' }}</el-descriptions-item>
        <el-descriptions-item label="时间">{{ detail.createTime }}</el-descriptions-item>
        <el-descriptions-item label="内容"><pre class="raw">{{ detail.rawContent || detail.payloadSummary }}</pre></el-descriptions-item>
      </el-descriptions>
    </el-dialog>

    <el-dialog title="设备接收报文开关" :visible.sync="receiveConfigVisible" width="760px" @open="loadReceiveConfigs">
      <div class="receive-config-toolbar">
        <el-input
          v-model="receiveConfigQuery.keyword"
          clearable
          size="small"
          placeholder="搜索设备名称、编码、站点或类型"
          style="width:280px"
          @keyup.enter.native="searchReceiveConfigs"
        />
        <el-button size="small" type="primary" icon="el-icon-search" @click="searchReceiveConfigs">查询</el-button>
      </div>
      <el-alert
        v-if="!recordReceiveEnabled"
        class="receive-config-alert"
        title="总开关已关闭，所有设备当前都不记录接收报文"
        type="warning"
        :closable="false"
        show-icon
      />
      <el-table :data="receiveConfigList" v-loading="receiveConfigLoading" border size="small">
        <el-table-column prop="deviceName" label="设备名称" min-width="150" show-overflow-tooltip />
        <el-table-column prop="terminalCode" label="终端编码" min-width="150" show-overflow-tooltip />
        <el-table-column prop="terminalType" label="设备类型" width="120" show-overflow-tooltip />
        <el-table-column prop="siteName" label="站点" min-width="150" show-overflow-tooltip />
        <el-table-column label="记录接收" width="120" align="center">
          <template slot-scope="{ row }">
            <el-switch
              :value="deviceRowReceiveEnabled(row)"
              :disabled="!recordReceiveEnabled || isReceiveSaving(row.terminalCode)"
              active-text="开"
              inactive-text="关"
              @change="onDeviceReceiveChange(row, $event)"
            />
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pager"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="receiveConfigTotal"
        :current-page="receiveConfigQuery.pageNum"
        :page-size="receiveConfigQuery.pageSize"
        :page-sizes="[10, 20, 50]"
        @size-change="handleReceiveConfigSizeChange"
        @current-change="handleReceiveConfigCurrentChange"
      />
    </el-dialog>
  </gas-page-shell>
</template>

<script>
import GasPageShell from '@/components/GasPageShell.vue'
import gasCrudPage from '@/mixins/gasCrudPage'
import { gasApi } from '@/api/gas'

export default {
  components: { GasPageShell },
  mixins: [gasCrudPage],
  data() {
    return {
      idField: 'messageId',
      listApi: gasApi.message.list,
      saveApi: null,
      removeApi: gasApi.message.remove,
      batchRemoveApi: gasApi.message.batchRemove,
      statCards: [],
      timeRange: null,
      dirChecks: ['down', 'up'],
      recordReceiveEnabled: true,
      recordSwitchSaving: false,
      receiveSwitchSaving: {},
      receiveConfigVisible: false,
      receiveConfigLoading: false,
      receiveConfigList: [],
      receiveConfigTotal: 0,
      receiveConfigQuery: { pageNum: 1, pageSize: 10, keyword: '' },
      detailVisible: false,
      detail: null,
      query: { pageNum: 1, pageSize: 10, terminalCode: '', directions: 'down,up', beginDate: '', endDate: '' }
    }
  },
  created() {
    this.loadStats()
  },
  methods: {
    async loadStats() {
      const res = await gasApi.message.stats()
      const d = res.data || {}
      if (d.recordReceiveEnabled !== undefined && d.recordReceiveEnabled !== null) {
        this.recordReceiveEnabled = !!d.recordReceiveEnabled
      }
      this.statCards = [
        { key: 't', label: '总报文数', value: d.total || 0, icon: 'el-icon-document', color: '#1677ff', bg: '#e8f3ff', valueColor: '#1677ff' },
        { key: 's', label: '发送报文', value: d.sent || 0, icon: 'el-icon-s-promotion', color: '#00b42a', bg: '#e8ffea', valueColor: '#00b42a' },
        { key: 'r', label: '接收报文', value: d.received || 0, icon: 'el-icon-download', color: '#722ed1', bg: '#f9f0ff', valueColor: '#722ed1' },
        { key: 'a', label: '异常报文', value: d.abnormal || 0, icon: 'el-icon-warning-outline', color: '#f53f3f', bg: '#ffece8', valueColor: '#f53f3f' }
      ]
    },
    buildQuery() {
      const p = { ...this.query }
      Object.keys(p).forEach(k => { if (p[k] === '') delete p[k] })
      return p
    },
    async loadData() {
      this.loading = true
      try {
        const res = await this.listApi(this.buildQuery())
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    refreshAll() {
      this.loadStats()
      this.loadData()
      if (this.receiveConfigVisible) this.loadReceiveConfigs()
    },
    search() {
      this.query.pageNum = 1
      this.refreshAll()
    },
    resetSearch() {
      this.timeRange = null
      this.dirChecks = ['down', 'up']
      this.query = { pageNum: 1, pageSize: 10, terminalCode: '', directions: 'down,up', beginDate: '', endDate: '' }
      this.refreshAll()
    },
    onTimeChange(val) {
      if (val && val.length === 2) {
        this.query.beginDate = val[0]
        this.query.endDate = val[1]
      } else {
        this.query.beginDate = ''
        this.query.endDate = ''
      }
    },
    onDirChange(val) {
      this.query.directions = val.length ? val.join(',') : undefined
    },
    async onRecordReceiveChange(val) {
      this.recordSwitchSaving = true
      try {
        await gasApi.sysConfig.save({
          'terminal.message.record.receive.enabled': val ? 'true' : 'false'
        })
        this.$message.success(val ? '已开启总开关，设备开关保持原设置' : '已关闭所有设备接收报文记录')
        if (!val) this.markAllReceiveRecordDisabled()
        this.refreshAll()
      } catch (e) {
        this.recordReceiveEnabled = !val
      } finally {
        this.recordSwitchSaving = false
      }
    },
    deviceRowReceiveEnabled(row) {
      if (!this.recordReceiveEnabled) return false
      if (row.deviceReceiveRecordEnabled === undefined || row.deviceReceiveRecordEnabled === null) return true
      return !!row.deviceReceiveRecordEnabled
    },
    isReceiveSaving(code) {
      return !!this.receiveSwitchSaving[this.receiveCode(code)]
    },
    receiveCode(code) {
      return code || 'unknown'
    },
    async saveReceiveConfig(code, enabled) {
      const terminalCode = this.receiveCode(code)
      this.$set(this.receiveSwitchSaving, terminalCode, true)
      try {
        const res = await gasApi.message.saveReceiveConfig({ terminalCode, enabled })
        const data = res.data || {}
        const deviceEnabled = data.deviceReceiveRecordEnabled !== undefined
          ? !!data.deviceReceiveRecordEnabled
          : !!enabled
        this.updateReceiveRows(terminalCode, deviceEnabled)
        this.$message.success(deviceEnabled ? '已开启该设备接收报文记录' : '已关闭该设备接收报文记录')
      } finally {
        this.$delete(this.receiveSwitchSaving, terminalCode)
      }
    },
    onDeviceReceiveChange(row, enabled) {
      if (!row || !this.recordReceiveEnabled) return
      this.saveReceiveConfig(row.terminalCode, enabled)
    },
    updateReceiveRows(terminalCode, deviceEnabled) {
      const code = this.receiveCode(terminalCode)
      this.list.forEach(row => {
        if (this.receiveCode(row.terminalCode) === code) {
          this.$set(row, 'deviceReceiveRecordEnabled', deviceEnabled)
          this.$set(row, 'receiveRecordEnabled', this.recordReceiveEnabled && deviceEnabled)
        }
      })
      this.receiveConfigList.forEach(row => {
        if (this.receiveCode(row.terminalCode) === code) {
          this.$set(row, 'deviceReceiveRecordEnabled', deviceEnabled)
          this.$set(row, 'receiveRecordEnabled', this.recordReceiveEnabled && deviceEnabled)
        }
      })
    },
    markAllReceiveRecordDisabled() {
      this.list.forEach(row => {
        this.$set(row, 'deviceReceiveRecordEnabled', false)
        this.$set(row, 'receiveRecordEnabled', false)
      })
      this.receiveConfigList.forEach(row => {
        this.$set(row, 'deviceReceiveRecordEnabled', false)
        this.$set(row, 'receiveRecordEnabled', false)
      })
    },
    openReceiveConfigDialog() {
      this.receiveConfigVisible = true
    },
    async loadReceiveConfigs() {
      this.receiveConfigLoading = true
      try {
        const res = await gasApi.message.receiveConfigList(this.receiveConfigQuery)
        const data = res.data || {}
        this.receiveConfigList = data.records || []
        this.receiveConfigTotal = Number(data.total || 0)
        if (data.globalReceiveRecordEnabled !== undefined && data.globalReceiveRecordEnabled !== null) {
          this.recordReceiveEnabled = !!data.globalReceiveRecordEnabled
        }
      } finally {
        this.receiveConfigLoading = false
      }
    },
    searchReceiveConfigs() {
      this.receiveConfigQuery.pageNum = 1
      this.loadReceiveConfigs()
    },
    handleReceiveConfigSizeChange(size) {
      this.receiveConfigQuery.pageSize = size
      this.receiveConfigQuery.pageNum = 1
      this.loadReceiveConfigs()
    },
    handleReceiveConfigCurrentChange(page) {
      this.receiveConfigQuery.pageNum = page
      this.loadReceiveConfigs()
    },
    hexContent(row) {
      return row.rawContent || row.payloadSummary || '—'
    },
    filterTerminal(code) {
      this.query.terminalCode = code
      this.search()
    },
    copyRow(row) {
      const text = this.hexContent(row)
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text)
      }
      this.$message.success('已复制报文内容')
    },
    viewDetail(row) {
      this.detail = { ...row }
      this.detailVisible = true
    },
    exportCsv() {
      this.$message.success('导出成功')
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/report-flow.scss';

.search-section {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
}
.section-title { display: flex; align-items: center; font-weight: 600; margin-bottom: 12px; }
.title-bar { width: 3px; height: 14px; background: #1677ff; margin-right: 8px; }
.message-search-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
.record-switch-item {
  margin-left: auto;
  margin-right: 0;
}
.device-switch-btn {
  margin-left: 8px;
}
.receive-config-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.receive-config-alert {
  margin-bottom: 12px;
}
.table-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.code-link { color: #1677ff; padding: 0; }
.dir-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}
.dir-tag.send { background: #e8ffea; color: #00b42a; }
.dir-tag.recv { background: #f9f0ff; color: #722ed1; }
.raw { margin: 0; white-space: pre-wrap; word-break: break-all; font-size: 12px; }
</style>
